/**
 * The scaffold engine: scene lifecycle, pointer input, undo history and the
 * render loop. Framework-free — React talks to it through the state stores and
 * the small command surface at the bottom of this file.
 */

import {
  Box3,
  Group,
  type Intersection,
  type Mesh,
  MeshStandardMaterial,
  type Object3D,
  Timer,
  Vector3,
} from "three";
import { nodeY, setBaseDatumMm } from "../data/haki.ts";
import { fail, type Result } from "../lib/result.ts";
import { groupFromPart, groupHasPart, type ScaffoldGroup } from "../model/connectivity.ts";
import { type GeneratorSettings, generate } from "../model/generator.ts";
import {
  clearModel,
  deserialize,
  ensureGuardrailPosts,
  removeStandard,
  type SerializedModel,
  serialize,
} from "../model/operations.ts";
import { legHeight, type PlankFamily } from "../model/solver.ts";
import { createModel, isMounted, type Leg, type Point, type ScaffoldModel } from "../model/types.ts";
import { type CameraRig, createCameraRig } from "./cameraRig.ts";
import { toGlb } from "./exportGlb.ts";
import { elemId, type PartRef, readPartRef, sameHighlightGroup } from "./partRef.ts";
import { Picker } from "./picking.ts";
import { BuildPlanes, type PlanePatch } from "./planes.ts";
import { ReferenceModel } from "./reference.ts";
import { type RenderResult, rebuild } from "./render/index.ts";
import { ScaffoldMover } from "./scaffoldMover.ts";
import { applySceneTheme, createSceneContext, type SceneContext, type SceneTheme } from "./scene.ts";
import { Terrain, type TerrainKind } from "./terrain.ts";
import { handleClick, handleRightClick } from "./tools/click.ts";
import { createGhostScene, type GhostScene, refreshGhosts } from "./tools/ghosts.ts";
import { handleHover } from "./tools/hover.ts";
import type { ElemHit, FreeEndHit, GhostHit, PlankHit, ToolContext } from "./tools/toolContext.ts";
import type { NodePayload, Pending, ToolSettings } from "./tools/types.ts";

/** Pointer travel that turns a click into a drag, pixels. */
const DRAG_THRESHOLD = 4;

/** Undo depth. */
const HISTORY_LIMIT = 60;

export type EngineEvents = Readonly<{
  onRender: (result: RenderResult) => void;
  onHud: (text: string | null, sticky: boolean) => void;
  onSelectLeg: (key: string | null) => void;
  onHistory: (canUndo: boolean, canRedo: boolean) => void;
  /** The gizmo moved a build plane; `done` once the drag has ended. */
  onPlaneEdit: (id: string, patch: PlanePatch, done: boolean) => void;
  onPlaneSelect: (id: string | null) => void;
}>;

/** A point in world metres. */
export type WorldPoint = Readonly<{ x: number; y: number; z: number }>;

/** Tracks whether a press became a drag — a still right-press deletes, a moved one panned. */
type DragState = { x: number; y: number; moved: boolean; button: number };

export class Engine implements ToolContext {
  readonly model: ScaffoldModel = createModel();
  readonly terrain = new Terrain();
  readonly reference = new ReferenceModel();
  readonly ghosts: GhostScene = createGhostScene();
  readonly planes: BuildPlanes;
  readonly mover: ScaffoldMover;

  settings: ToolSettings = {
    tool: "nav",
    size: 3000,
    beamKind: "lb",
    accKind: "luke",
    railKind: "both",
    bracketKind: "sk",
    deckKind: "bay",
    plankFam: "mix",
    autoSpir: true,
    snap: true,
  };
  plankFamily: PlankFamily = "mix";
  pending: Pending | null = null;

  private readonly scaffold = new Group();
  private readonly timer = new Timer();
  private readonly events: EngineEvents;
  private rig: CameraRig | null = null;
  private scene: SceneContext | null = null;
  private picker: Picker | null = null;
  private host: HTMLElement | null = null;
  private frame = 0;
  private past: string[] = [];
  private future: string[] = [];
  private selectedLegKey: string | null = null;
  private hotMeshes: Mesh[] = [];
  private hotOriginals = new Map<Mesh, Mesh["material"]>();
  private hotId: string | null = null;
  private drag: DragState | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private latest: RenderResult | null = null;
  private hoverGroup: ScaffoldGroup | null = null;
  private referenceCollides = true;

  constructor(events: EngineEvents) {
    this.events = events;
    this.planes = new BuildPlanes({ onEdit: (id, patch, done) => events.onPlaneEdit(id, patch, done) });
    this.mover = new ScaffoldMover({
      model: this.model,
      snapEnabled: () => this.settings.snap,
      groundY: (x, z) => this.terrain.groundY(x, z),
      snapshot: () => this.snapshot(),
      rebuild: () => this.rebuild(),
      hud: (text, sticky) => this.hud(text, sticky),
      onSelectionChange: () => this.repaintGroups(),
    });
  }

  /* ---------------------------------------------------------------------------
     Lifecycle
     --------------------------------------------------------------------------- */

  mount(host: HTMLElement, theme: SceneTheme): () => void {
    const scene = createSceneContext(theme);
    this.scene = scene;
    this.host = host;
    host.appendChild(scene.renderer.domElement);

    scene.scene.add(
      this.terrain.group,
      this.reference.group,
      this.planes.group,
      this.scaffold,
      this.ghosts.root,
    );
    const picker = new Picker(scene.renderer.domElement, scene.camera);
    this.picker = picker;
    const rig = createCameraRig(scene.camera, scene.renderer.domElement);
    this.rig = rig;
    // Page Visibility: a hidden tab would otherwise return one huge delta.
    this.timer.connect(document);
    this.terrain.build("flat");

    const canvas = scene.renderer.domElement;
    canvas.style.display = "block";
    canvas.addEventListener("contextmenu", preventDefault);
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointerleave", this.onPointerLeave);
    this.planes.attach(scene.scene, scene.camera, canvas, rig, picker);
    this.planes.setGizmoEnabled(this.settings.tool === "plane");
    this.mover.attach(scene.scene, scene.camera, canvas, rig);
    this.mover.setActive(this.settings.tool === "move");

    const observer = new ResizeObserver(() => this.resize());
    observer.observe(host);
    this.resize();

    const loop = (): void => {
      this.frame = requestAnimationFrame(loop);
      this.timer.update();
      this.rig?.update(this.timer.getDelta());
      this.planes.update(scene.camera);
      scene.renderer.render(scene.scene, scene.camera);
    };
    this.frame = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(this.frame);
      observer.disconnect();
      canvas.removeEventListener("contextmenu", preventDefault);
      canvas.removeEventListener("pointerdown", this.onPointerDown);
      canvas.removeEventListener("pointermove", this.onPointerMove);
      canvas.removeEventListener("pointerup", this.onPointerUp);
      canvas.removeEventListener("pointerleave", this.onPointerLeave);
      this.planes.detach();
      this.mover.detach();
      this.rig?.dispose();
      this.rig = null;
      this.timer.dispose();
      canvas.remove();
      scene.dispose();
      this.scene = null;
      this.picker = null;
      this.host = null;
    };
  }

  private resize(): void {
    const scene = this.scene;
    if (!scene || !this.host) {
      return;
    }

    const width = this.host.clientWidth;
    const height = this.host.clientHeight;
    if (width === 0 || height === 0) {
      return;
    }

    scene.renderer.setSize(width, height);
    scene.camera.aspect = width / height;
    scene.camera.updateProjectionMatrix();
  }

  setTheme(theme: SceneTheme): void {
    if (this.scene) {
      applySceneTheme(this.scene.scene, theme);
    }
  }

  /* ---------------------------------------------------------------------------
     ToolContext
     --------------------------------------------------------------------------- */

  pickGhost(event: PointerEvent): GhostHit | null {
    const hit = this.picker?.first(event, this.ghosts.pickGhosts);
    const mesh = hit?.object;
    if (!hit || !mesh) {
      return null;
    }

    const payload = this.ghosts.payloads.get(mesh.uuid);
    if (!payload || !isMesh(mesh)) {
      return null;
    }

    return { mesh, payload, point: hit.point };
  }

  pickNode(event: PointerEvent): NodePayload | null {
    const hit = this.picker?.first(event, this.ghosts.pickNodes);
    if (!hit) {
      return null;
    }

    return this.ghosts.nodes.get(hit.object.uuid) ?? null;
  }

  pickElem(event: PointerEvent, accept?: (ref: PartRef) => boolean): ElemHit | null {
    const targets = this.latest?.pickElems ?? [];
    const filtered = accept
      ? targets.filter((mesh) => {
          const ref = readPartRef(mesh.userData);
          return ref !== null && accept(ref);
        })
      : targets;

    const hit = this.picker?.first(event, filtered);
    if (!hit) {
      return null;
    }

    const ref = readPartRef(hit.object.userData);

    return ref ? { ref, point: hit.point } : null;
  }

  pickPlank(event: PointerEvent): PlankHit | null {
    const hit = this.picker?.first(event, this.latest?.pickPlanks ?? []);
    const ref = hit ? readPartRef(hit.object.userData) : null;
    if (ref?.type !== "plank") {
      return null;
    }

    return { idx: ref.idx, slot: ref.slot, fam: ref.fam };
  }

  pickFreeEnd(event: PointerEvent): FreeEndHit | null {
    const hit = this.picker?.first(event, this.latest?.pickEnds ?? []);
    const ref = hit ? readPartRef(hit.object.userData) : null;
    if (ref?.type !== "freeEnd") {
      return null;
    }

    return { x: ref.x, z: ref.z, p: ref.p };
  }

  pickCollider(event: PointerEvent): Intersection | null {
    return this.picker?.first(event, this.terrain.colliders) ?? null;
  }

  pickPlane(event: PointerEvent): string | null {
    const hit = this.picker?.first(event, this.planes.pickMeshes);

    return hit ? this.planes.planeIdOf(hit.object) : null;
  }

  /** Selection is store state; the store pushes it back through `planes.sync`. */
  selectPlane(id: string | null): void {
    this.events.onPlaneSelect(id);
  }

  wasPressOnGizmo(): boolean {
    return this.planes.wasPressOnGizmo() || this.mover.wasPressOnGizmo();
  }

  isPointerOnGizmo(): boolean {
    return this.planes.isPointerOnGizmo() || this.mover.isPointerOnGizmo();
  }

  selectScaffold(group: ScaffoldGroup | null): void {
    this.mover.select(group);
  }

  selectedScaffold(): ScaffoldGroup | null {
    return this.mover.selection;
  }

  /**
   * Nearest hit on anything visible — scaffold parts, terrain and the reference
   * model, whether or not it is currently a collider. Used to re-pivot.
   */
  private pickAnything(event: PointerEvent): Intersection | null {
    const targets = [
      ...(this.latest?.pickElems ?? []),
      ...this.terrain.colliders,
      ...this.reference.colliderMeshes,
      ...this.planes.pickMeshes,
    ];

    return this.picker?.first(event, targets) ?? null;
  }

  /**
   * Alt+click: orbit around what you clicked. The camera does not move, so the
   * view stays put and only the pivot changes.
   */
  private setPivotAt(event: PointerEvent): boolean {
    const hit = this.pickAnything(event);
    if (!hit || !this.rig) {
      return false;
    }

    this.rig.setPivot(hit.point);
    this.hud(`Orbit target: ${hit.point.x.toFixed(2)}, ${hit.point.y.toFixed(2)}, ${hit.point.z.toFixed(2)}`);

    return true;
  }

  planeAt(event: PointerEvent, y: number): Point | null {
    return this.picker?.onPlane(event, y) ?? null;
  }

  hoveredLeg(event: PointerEvent): Leg | null {
    const node = this.pickNode(event);
    if (node) {
      return node.leg;
    }

    const hit = this.pickElem(event, (ref) => ref.type === "leg");
    if (hit?.ref.type === "leg") {
      return this.model.legs.get(hit.ref.key) ?? null;
    }

    return null;
  }

  hud(text: string, sticky = false): void {
    this.events.onHud(text, sticky);
  }

  hideHud(): void {
    this.events.onHud(null, false);
  }

  isRightPressed(): boolean {
    return this.drag?.button === 2 && !this.drag.moved;
  }

  selectLeg(key: string | null): void {
    this.selectedLegKey = key;
    this.events.onSelectLeg(key);
  }

  setHot(ref: PartRef): void {
    const id = elemId(ref);
    if (id === this.hotId) {
      return;
    }

    this.clearHot();
    this.hotId = id;
    this.paint((meshRef) => sameHighlightGroup(ref, meshRef), HOT_MATERIAL);
    this.setCursor("pointer");
  }

  /**
   * Restore every painted mesh, then put the Move tool's selection back — it
   * is the one highlight that outlives a hover.
   */
  clearHot(): void {
    for (const mesh of this.hotMeshes) {
      const original = this.hotOriginals.get(mesh);
      if (original) {
        mesh.material = original;
      }
    }
    this.hotMeshes = [];
    this.hotOriginals = new Map();
    this.hotId = null;
    this.hoverGroup = null;
    this.setCursor("");
    this.paint((ref) => this.mover.isSelectedPart(ref), MOVE_MATERIAL);
  }

  /**
   * Paint the meshes `accept` picks. A mesh's true material is remembered the
   * first time it is painted, so layers — hover over selection — restore cleanly.
   */
  private paint(accept: (ref: PartRef) => boolean, material: MeshStandardMaterial): void {
    for (const mesh of this.latest?.pickElems ?? []) {
      const ref = readPartRef(mesh.userData);
      if (!ref || !accept(ref)) {
        continue;
      }

      if (!this.hotOriginals.has(mesh)) {
        this.hotOriginals.set(mesh, mesh.material);
      }
      mesh.material = material;
      this.hotMeshes.push(mesh);
    }
  }

  /** The selection changed or the meshes were rebuilt: drop the hover paint and repaint the selection. */
  private repaintGroups(): void {
    this.hoverGroup = null;
    this.clearHot();
  }

  private setCursor(value: string): void {
    if (this.scene) {
      this.scene.renderer.domElement.style.cursor = value;
    }
  }

  /* ---------------------------------------------------------------------------
     Model commands
     --------------------------------------------------------------------------- */

  /** The model as it stands, for the history stacks. */
  private currentState(): string {
    return JSON.stringify(serialize(this.model));
  }

  private notifyHistory(): void {
    this.events.onHistory(this.past.length > 0, this.future.length > 0);
  }

  /**
   * Record the state *before* an edit.
   *
   * Consecutive identical states are dropped: several actions snapshot and then
   * bail (placing a standard where one already stands, for instance), which
   * would otherwise leave undo steps that appear to do nothing.
   */
  snapshot(): void {
    // An edit is starting, so anything that was undone is no longer reachable.
    // Cleared before the dedupe below, so it holds however that returns.
    this.future = [];

    const state = this.currentState();
    if (this.past[this.past.length - 1] !== state) {
      this.past.push(state);
      if (this.past.length > HISTORY_LIMIT) {
        this.past.shift();
      }
    }

    this.notifyHistory();
  }

  /** Restore `state`, moving the state it replaces onto `onto`. */
  private applyHistory(state: string, onto: string[]): void {
    onto.push(this.currentState());
    deserialize(this.model, JSON.parse(state));
    this.pending = null;
    this.selectLeg(null);
    this.mover.select(null);
    this.rebuild();
    this.notifyHistory();
  }

  undo(): void {
    const previous = this.past.pop();
    if (previous === undefined) {
      this.hud("Nothing to undo");
      return;
    }

    this.applyHistory(previous, this.future);
    this.hud("Undone");
  }

  redo(): void {
    const next = this.future.pop();
    if (next === undefined) {
      this.hud("Nothing to redo");
      return;
    }

    this.applyHistory(next, this.past);
    this.hud("Redone");
  }

  clearHistory(): void {
    this.past = [];
    this.future = [];
    this.notifyHistory();
  }

  rebuild(): void {
    this.latest = rebuild({
      model: this.model,
      terrain: this.terrain,
      root: this.scaffold,
      selectedLegKey: this.selectedLegKey,
      plankFam: this.plankFamily,
    });
    this.refreshGhosts();
    this.events.onRender(this.latest);
    this.repaintGroups();
  }

  refreshGhosts(): void {
    refreshGhosts(this.ghosts, this.model, this.terrain, this.settings, this.pending);
  }

  setSettings(settings: ToolSettings): void {
    const toolChanged = settings.tool !== this.settings.tool;
    this.settings = settings;
    if (toolChanged) {
      this.pending = null;
      if (settings.tool !== "spir") {
        this.selectLeg(null);
      }
      this.planes.setGizmoEnabled(settings.tool === "plane");
      this.mover.setActive(settings.tool === "move");
      if (settings.tool !== "move") {
        this.mover.select(null);
      }
    }
    this.refreshGhosts();
  }

  /* ---------------------------------------------------------------------------
     Build planes
     --------------------------------------------------------------------------- */

  /** The planes changed shape or standability: refresh what a standard can stand on and re-solve every leg. */
  afterPlanesChange(): void {
    this.terrain.setPlaneColliders(this.planes.colliderMeshes);
    this.rebuild();
  }

  framePlane(id: string): boolean {
    const box = this.planes.boundsOf(id);
    if (!box || !this.rig) {
      return false;
    }

    this.rig.fit(box);

    return true;
  }

  /** Where the camera orbits — a new plane appears there so it lands in view. */
  viewTarget(): WorldPoint {
    const target = this.rig?.controls.getTarget(new Vector3()) ?? new Vector3();

    return { x: target.x, y: target.y, z: target.z };
  }

  setPlankFamily(family: PlankFamily): void {
    this.plankFamily = family;
    this.rebuild();
  }

  /**
   * Move the base datum. Every leg re-solves its base level and jack extension
   * on the next render, so the whole scaffold rises or settles together.
   * Returns the value actually applied after clamping.
   */
  setBaseDatum(mm: number): number {
    const applied = setBaseDatumMm(mm);
    this.rebuild();

    return applied;
  }

  setTerrain(kind: TerrainKind): void {
    this.terrain.build(kind);
    this.syncReferenceColliders();
    this.rebuild();
  }

  setReferenceCollides(collides: boolean): void {
    this.referenceCollides = collides;
    this.syncReferenceColliders();
    this.rebuild();
  }

  private syncReferenceColliders(): void {
    this.terrain.setReferenceColliders(this.referenceCollides ? this.reference.colliderMeshes : []);
  }

  generate(settings: GeneratorSettings): void {
    this.snapshot();
    generate(this.model, settings);
    // Guardrails need a standard reaching their top connector; the run above the
    // top lift is not tall enough on its own.
    ensureGuardrailPosts(this.model, (x, z) => this.terrain.groundY(x, z));
    this.pending = null;
    this.selectLeg(null);
    this.mover.select(null);
    this.rebuild();
    this.frameScaffold();
  }

  clear(): void {
    this.snapshot();
    clearModel(this.model);
    this.pending = null;
    this.selectLeg(null);
    this.mover.select(null);
    this.rebuild();
  }

  cancelPending(): void {
    this.pending = null;
    if (this.planes.selectedId !== null) {
      this.selectPlane(null);
    }
    if (this.mover.selection) {
      this.mover.select(null);
    }
    this.refreshGhosts();
    this.hud("Cancelled");
  }

  /**
   * The scaffold as binary glTF, at its world coordinates.
   *
   * Free-end markers and planks the user has taken out are viewport
   * affordances rather than components, so they are excluded.
   */
  async exportGlb(): Promise<Result<ArrayBuffer>> {
    if (this.model.legs.size === 0) {
      return fail("Nothing to export — the scaffold is empty");
    }

    const hide: Object3D[] = [...(this.latest?.pickEnds ?? [])];
    for (const plank of this.latest?.pickPlanks ?? []) {
      const ref = readPartRef(plank.userData);
      if (ref?.type === "plank" && this.model.decks[ref.idx]?.removed.has(ref.slot)) {
        hide.push(plank);
      }
    }

    return toGlb(this.scaffold, { hide });
  }

  serialize(settings?: Record<string, unknown>): SerializedModel {
    return serialize(this.model, settings);
  }

  load(raw: unknown): void {
    this.snapshot();
    deserialize(this.model, raw);
    this.pending = null;
    this.selectLeg(null);
    this.mover.select(null);
    this.rebuild();
    this.frameScaffold();
  }

  stackSelected(size: number): void {
    const leg = this.selectedLegKey ? this.model.legs.get(this.selectedLegKey) : null;
    if (!leg) {
      return;
    }

    this.snapshot();
    leg.segments.push(size);
    this.rebuild();
    this.selectLeg(this.selectedLegKey);
  }

  popSelected(): void {
    const leg = this.selectedLegKey ? this.model.legs.get(this.selectedLegKey) : null;
    if (!leg) {
      return;
    }
    // A standard on a bracket may be stripped to the bare stub; a ground one keeps a segment.
    if (leg.segments.length <= (isMounted(leg) ? 0 : 1)) {
      this.hud(isMounted(leg) ? "Nothing left on the bracket" : "A standard needs at least one segment");
      return;
    }

    this.snapshot();
    leg.segments.pop();
    this.rebuild();
    this.selectLeg(this.selectedLegKey);
  }

  removeSelected(): void {
    const leg = this.selectedLegKey ? this.model.legs.get(this.selectedLegKey) : null;
    if (!leg) {
      return;
    }

    this.snapshot();
    removeStandard(this.model, leg);
    this.selectLeg(null);
    this.rebuild();
  }

  selectedLeg(): { key: string; x: number; z: number; segments: number[]; height: number } | null {
    const key = this.selectedLegKey;
    const leg = key ? this.model.legs.get(key) : null;
    if (!key || !leg) {
      return null;
    }

    return { key, x: leg.x, z: leg.z, segments: [...leg.segments], height: legHeight(leg) };
  }

  /** Frame the whole scaffold, or fall back to the default view when it is empty. */
  frameScaffold(): void {
    if (!this.rig) {
      return;
    }
    if (this.model.legs.size === 0) {
      this.rig.reset(true);
      return;
    }

    const box = new Box3();
    for (const leg of this.model.legs.values()) {
      const base = leg.gy ?? 0;
      box.expandByPoint(new Vector3(leg.x, base, leg.z));
      box.expandByPoint(new Vector3(leg.x, nodeY(leg.baseP ?? 0) + legHeight(leg), leg.z));
    }
    this.rig.fit(box);
  }

  /** Point the camera at the imported reference model. */
  frameReference(): boolean {
    const bounds = this.reference.bounds;
    if (!bounds || !this.rig) {
      return false;
    }

    this.rig.fit(bounds);

    return true;
  }

  afterReferenceChange(): void {
    this.syncReferenceColliders();
    this.rebuild();
  }

  /* ---------------------------------------------------------------------------
     Moving a whole scaffold
     --------------------------------------------------------------------------- */

  /** The scaffold under the pointer, for the Move tool's hover highlight. */
  groupUnder(event: PointerEvent): ScaffoldGroup | null {
    const hit = this.pickElem(event);

    return hit ? groupFromPart(this.model, hit.ref) : null;
  }

  /** Paint the scaffold under the pointer; the selected scaffold stays painted beneath it. */
  highlightGroup(group: ScaffoldGroup | null): void {
    if (group === this.hoverGroup) {
      return;
    }

    this.clearHot();
    this.hoverGroup = group;
    if (!group) {
      return;
    }

    this.paint((ref) => groupHasPart(group, ref), MOVE_MATERIAL);
    this.setCursor("pointer");
  }

  /* ---------------------------------------------------------------------------
     Pointer input
     --------------------------------------------------------------------------- */

  private readonly onPointerDown = (event: PointerEvent): void => {
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.drag = { x: event.clientX, y: event.clientY, moved: false, button: event.button };

    if (event.button === 2) {
      handleHover(this, event);
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    const tracked = this.pointers.get(event.pointerId);
    if (tracked) {
      tracked.x = event.clientX;
      tracked.y = event.clientY;
    }

    if (!this.drag) {
      handleHover(this, event);
      return;
    }

    // Past the threshold the press is a camera move — camera-controls is already
    // applying it, so all this tracks is that the press stopped being a click.
    const travel = Math.abs(event.clientX - this.drag.x) + Math.abs(event.clientY - this.drag.y);
    if (travel > DRAG_THRESHOLD) {
      if (!this.drag.moved && this.drag.button === 2) {
        this.clearHot();
      }
      this.drag.moved = true;
      return;
    }

    if (this.drag.button === 2) {
      handleHover(this, event);
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    this.pointers.delete(event.pointerId);

    if (this.drag && !this.drag.moved) {
      const repivoted = this.drag.button === 0 && event.altKey && this.setPivotAt(event);
      if (!repivoted) {
        if (this.drag.button === 0) {
          handleClick(this, event);
        } else if (this.drag.button === 2) {
          handleRightClick(this, event);
        }
      }
    }
    if (this.pointers.size === 0) {
      this.drag = null;
    }
  };

  private readonly onPointerLeave = (): void => {
    if (this.ghosts.spirGhost) {
      this.ghosts.spirGhost.visible = false;
    }
    if (this.ghosts.aimGhost) {
      this.ghosts.aimGhost.visible = false;
    }
    this.clearHot();
  };
}

const MOVE_MATERIAL = new MeshStandardMaterial({
  color: 0x0a4da3,
  emissive: 0x0a4da3,
  emissiveIntensity: 0.5,
  metalness: 0.4,
  roughness: 0.5,
});

const HOT_MATERIAL = new MeshStandardMaterial({
  color: 0xb03a1e,
  emissive: 0xb03a1e,
  emissiveIntensity: 0.55,
  metalness: 0.3,
  roughness: 0.5,
});

function preventDefault(event: Event): void {
  event.preventDefault();
}

function isMesh(object: unknown): object is Mesh {
  return typeof object === "object" && object !== null && "isMesh" in object;
}
